import { Suspense, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useWahaRealtime } from "@/hooks/use-waha-realtime";
import { useWahaHealthCheck } from "@/hooks/use-waha-health-check";
import { useAppTheme } from "@/hooks/use-app-theme";
import { WahaReconnectBanner } from "@/components/WahaReconnectBanner";
import { ProductTourProvider } from "@/components/product-tour/ProductTourProvider";
import { ProductTourOverlay } from "@/components/product-tour/ProductTourOverlay";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PageSkeleton } from "@/components/PageSkeleton";
import { JulietteRightPanel } from "@/components/juliette/JulietteRightPanel";
import { JulietteDeeplinkConsumer } from "@/components/juliette/JulietteDeeplinkConsumer";
import {
  JulietteLauncherProvider,
  useJulietteLauncher,
} from "@/hooks/use-juliette-launcher";
import { GuidanceNudgeProvider } from "@/hooks/use-guidance-nudge";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { useCompressionNotification } from "@/hooks/use-compression-notification";
import { useHotSignalRealtime } from "@/hooks/useHotSignalRealtime";
import { usePushActionListener } from "@/hooks/usePushActionListener";
import { S1Logo } from "@/components/S1Logo";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { InstallBlocker } from "@/components/onboarding/InstallBlocker";
import { cn } from "@/lib/utils";

const PAGE_TITLES: Array<{ match: string; title: string; exact?: boolean }> = [
  { match: "/", title: "Tableau de bord", exact: true },
  { match: "/prospection/terrain", title: "Terrain humain" },
  { match: "/prospection/cadastral", title: "Cadastral automatisé" },
  { match: "/prospection", title: "Prospection" },
  { match: "/scraping", title: "Sniper Web" },
  { match: "/crm", title: "CRM" },
  { match: "/messages", title: "Messagerie" },
  { match: "/media", title: "Bibliothèque" },
  { match: "/vitrine", title: "Site web" },
  { match: "/referral", title: "Ambassadeurs" },
  { match: "/campaigns/templates", title: "Campagnes" },
  { match: "/campaigns/branding", title: "Branding" },
  { match: "/campaigns", title: "Pilotage" },
  { match: "/settings", title: "Paramètres" },
  { match: "/compliance", title: "Conformité" },
  { match: "/onboarding", title: "Configuration" },
];

function getPageTitle(pathname: string): string {
  for (const entry of PAGE_TITLES) {
    if (entry.exact ? pathname === entry.match : pathname.startsWith(entry.match)) {
      return entry.title;
    }
  }
  return "Systeme1";
}

/**
 * Replie la sidebar en rail d'icônes sur tablette (768–1023px) et la déploie
 * sur desktop (>=1024px). Sous 768px, c'est le drawer mobile (openMobile),
 * on n'y touche pas. Les écouteurs matchMedia ne se déclenchent qu'au
 * franchissement d'un breakpoint : l'utilisateur garde la main sur le toggle
 * à l'intérieur d'une plage. Doit être monté DANS le SidebarProvider.
 */
function SidebarTabletCollapse(): null {
  const { setOpen } = useSidebar();
  const setOpenRef = useRef(setOpen);
  setOpenRef.current = setOpen;

  useEffect(() => {
    const tablet = window.matchMedia("(min-width: 768px) and (max-width: 1023px)");
    const desktop = window.matchMedia("(min-width: 1024px)");
    const apply = () => {
      if (tablet.matches) setOpenRef.current(false);
      else if (desktop.matches) setOpenRef.current(true);
    };
    apply();
    tablet.addEventListener("change", apply);
    desktop.addEventListener("change", apply);
    return () => {
      tablet.removeEventListener("change", apply);
      desktop.removeEventListener("change", apply);
    };
  }, []);

  return null;
}

/**
 * Coquille interne montée DANS le JulietteLauncherProvider : elle peut donc lire
 * l'état du launcher pour "pousser" le contenu quand le panneau droit est ouvert.
 */
function ProtectedShell({ pathname }: { pathname: string }) {
  const { isOpen, mode } = useJulietteLauncher();
  const panelOpen = isOpen && mode === "panel";

  return (
    <>
      <SidebarTabletCollapse />
      <div className="flex min-h-[100dvh] w-full">
        <AppSidebar />
        <main
          className={cn(
            "flex-1 flex flex-col min-w-0 bg-gradient-to-br from-background via-background to-primary/[0.06] pb-16 md:pb-0 transition-[margin] duration-300 ease-out",
            panelOpen && "md:mr-[380px]",
          )}
        >
          <header className="sticky top-0 z-50 flex min-h-[calc(3.5rem+var(--safe-area-top))] pt-[var(--safe-area-top)] items-center gap-2 border-b border-border px-3 bg-card/90 backdrop-blur-lg md:hidden">
            <SidebarTrigger />
            <S1Logo variant="full" height={24} />
            <span className="text-sm font-semibold tracking-tight text-foreground truncate flex-1">
              {getPageTitle(pathname)}
            </span>
          </header>
          <WahaReconnectBanner />
          <div className="flex-1 overflow-x-hidden p-3 sm:p-4 md:p-6 animate-slide-in page-depth-bg">
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
      <MobileBottomNav />
      <JulietteRightPanel />
      <JulietteDeeplinkConsumer />
      <InstallPrompt />
      <InstallBlocker />
      <ProductTourOverlay />
    </>
  );
}

export function ProtectedLayout() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useWahaRealtime();
  useWahaHealthCheck();
  useAppTheme();
  useCompressionNotification();
  useHotSignalRealtime();
  usePushActionListener();

  return (
    <ProtectedRoute>
      <SidebarProvider>
        <ProductTourProvider>
          <JulietteLauncherProvider>
            <GuidanceNudgeProvider>
              <ProtectedShell pathname={pathname} />
            </GuidanceNudgeProvider>
          </JulietteLauncherProvider>
        </ProductTourProvider>
      </SidebarProvider>
    </ProtectedRoute>
  );
}
